import { useFlags } from '@features/feature-flags';
import { PageHeader, TabStrip } from '@shared/ui';
import { Outlet } from 'react-router';

const BASE = '/catalog';

/**
 * What exists on the platform, with no tenant in sight.
 *
 * The other half of the split this screen exists for is `Client → …`, which
 * says what one museum has. Keeping the catalog and the assignment on separate
 * screens is what Stripe does with products and subscriptions, Auth0 with APIs
 * and user grants, and LaunchDarkly with flags and targeting: in all three the
 * thing and who has it never share a page, because they are edited by different
 * people for different reasons.
 *
 * No tab is the index. Every one has its own path, so `/catalog` is a real URL
 * with nothing to mark — which is why the router redirects it to
 * `/catalog/flags` rather than rendering a strip with no tab lit.
 *
 * The counts sit on the tabs rather than on the sidebar row. A number beside
 * `Catalog` would have to stand for three things at once and would read as
 * whichever one the reader had in mind; beside `Flags` it means flags. They
 * share the flags query with the list below, so the tab and the table it opens
 * cannot disagree.
 */
export function CatalogLayout() {
    const flags = useFlags();

    return (
        <>
            <PageHeader
                eyebrow="Configuration"
                title="Catalog"
                description="Everything the platform can grant a museum. Flags are the switches themselves; components and products group them into things Product and Sales can talk about."
            />
            <TabStrip
                label="Catalog sections"
                base={BASE}
                tabs={[
                    { to: `${BASE}/flags`, label: 'Flags', count: flags.data?.length },
                    // Zero, and true: the catalogs exist as screens before they
                    // hold anything. Omitting the number would read as "not
                    // counted yet" rather than "none".
                    { to: `${BASE}/components`, label: 'Components', count: 0 },
                    { to: `${BASE}/products`, label: 'Products', count: 0 },
                ]}
            />
            <Outlet />
        </>
    );
}
